(function (scope) {
  'use strict';
  const giftLocks = new Set();
  const same = (a, b) => String(a) === String(b);
  function mappedGiftVariant(choice, variant, purchasable) {
    const mapping = choice?.mappings?.find(m => same(m.originalVariantId, variant?.id));
    return purchasable(variant) && mapping?.cloneAvailable === true && mapping.clonePrice === 0 ? mapping : null;
  }
  function bundleQuantity(host, offer) {
    if (!offer) return 0;
    const eligible = new Set((offer.eligibleProductIds || []).map(String));
    let count = eligible.has(String(host.config.main.id)) ? host.quantity() || 0 : 0;
    for (const item of host.items) if (item.selected && eligible.has(String(item.product.id))) count++;
    return count;
  }
  function quantityOfferActive(host, offer, now = Date.now()) {
    if (!offer || host.config.countryCode !== 'US' || host.config.currency !== 'USD') return false;
    const start = Date.parse(offer.startsAt), end = offer.endsAt ? Date.parse(offer.endsAt) : Infinity;
    return offer.code === 'HE10' && offer.discountType === 'percentage' &&
      Number.isFinite(offer.discountPercentage) && offer.discountPercentage > 0 && offer.discountPercentage <= 100 &&
      Number.isInteger(offer.minimumQuantity) && offer.minimumQuantity >= 2 &&
      offer.appliesOnOneTimePurchase === true && Number.isFinite(start) && now >= start && now < end;
  }
  function quantityOfferEligible(host, offer, product, now = Date.now()) {
    return quantityOfferActive(host, offer, now) && (offer.eligibleProductIds || []).some(id => same(id, product?.id));
  }
  function quantityOfferQuantity(host, offer) {
    if (!quantityOfferActive(host, offer)) return 0;
    const plan = host.form?.elements?.namedItem('selling_plan')?.value || host.form?.querySelector?.('[name="selling_plan"]')?.value;
    let count = !plan && quantityOfferEligible(host, offer, host.config.main) ? host.quantity() || 0 : 0;
    for (const item of host.items) if (item.selected && item.live !== false && quantityOfferEligible(host, offer, item.product)) count++;
    return count;
  }
  function existingGift(cart, offer, expectedId = null) {
    const ids = new Set((offer?.choices || []).flatMap(c => c.mappings || []).map(m => String(m.cloneVariantId)));
    return (cart?.items || []).find(line => line.quantity > 0 && ids.has(String(line.variant_id)) && (expectedId == null || same(line.variant_id, expectedId)));
  }
  scope.KmFbtPromotionLogic = { mappedGiftVariant, bundleQuantity, existingGift, quantityOfferActive, quantityOfferEligible, quantityOfferQuantity };
  class KmFbtPromotions {
    constructor(host, logic) {
      this.host = host; this.logic = logic;
      this.offer = host.config.giftOffer;
      this.bundle = host.config.bundleOffer;
      this.quantityOffer = host.config.quantityOffer;
      this.choices = (this.offer?.choices || []).filter(c => c.product?.variants?.some(v => mappedGiftVariant(c, v, logic.isPurchasable))).map(c => ({ ...c, selected: false, choices: (c.product.options || []).map(o => o.values.length === 1 ? o.values[0] : null) }));
      if (this.bundle) {
        this.banner = host.el('p', 'km-fbt__bundle-banner');
        host.insertBefore(this.banner, host.querySelector('.km-fbt__card'));
      }
      if (this.choices.length) {
        this.giftSection = host.el('dialog', 'km-fbt__gifts');
        this.giftSection.id = `km-fbt-gifts-${host.dataset.formId || host.config.main.id}`;
        this.giftSection.setAttribute('aria-modal', 'true');
        this.giftSection.append(host.el('style', '', `
          dialog.km-fbt__gifts .km-gift-display { display:block; position:relative; box-sizing:border-box; border:1px solid #ddd; border-radius:12px; background:#fff; color:#222; margin:12px 0; padding:38px 18px 18px; min-width:0; width:100%; }
          dialog.km-fbt__gifts .km-gift-display__row { display:grid; grid-template-columns:96px minmax(0,1fr) max-content; gap:18px; align-items:center; min-width:0; }
          dialog.km-fbt__gifts .km-gift-display__image { display:block; position:static; width:96px; height:112px; max-width:100%; margin:0; object-fit:contain; object-position:center; }
          dialog.km-fbt__gifts .km-gift-display__title { display:block; position:static; width:auto; min-width:0; max-width:none; height:auto; max-height:none; overflow:visible; white-space:normal; overflow-wrap:anywhere; word-break:normal; -webkit-line-clamp:unset; font-size:16px; line-height:1.5; color:inherit; }
          dialog.km-fbt__gifts .km-gift-display__price { display:block; white-space:nowrap; font-size:17px; line-height:1.4; }
          dialog.km-fbt__gifts .km-gift-display__badge { position:absolute; right:0; top:0; border-radius:0 11px 0 11px; padding:5px 11px; background:#268343; color:#fff; font-size:12px; line-height:1.5; white-space:nowrap; }
          @media(max-width:600px) {
            dialog.km-fbt__gifts .km-gift-display { padding:36px 12px 14px; }
            dialog.km-fbt__gifts .km-gift-display__row { grid-template-columns:72px minmax(0,1fr); gap:8px 12px; }
            dialog.km-fbt__gifts .km-gift-display__image { width:72px; height:92px; grid-column:1; grid-row:1 / 3; }
            dialog.km-fbt__gifts .km-gift-display__title { grid-column:2; font-size:14px; }
            dialog.km-fbt__gifts .km-gift-display__price { grid-column:2; font-size:16px; }
          }
        `));
        const heading = host.el('h4', 'km-fbt__gift-heading', 'Buy this item, get 1 free gift');
        heading.style.cssText = 'font-size:22px;line-height:1.35;letter-spacing:normal;text-transform:none;margin:0 44px 8px 0;';
        heading.id = `${this.giftSection.id}-heading`;
        this.giftSection.setAttribute('aria-labelledby', heading.id);
        heading.tabIndex = -1;
        this.giftSection.append(heading);
        const explanation = host.el('p', 'km-gift-display__intro', 'After adding this item to your cart, a gift selection pop-up will appear. Choose your free gift there and add it to your cart. The gift is not added automatically.');
        explanation.style.cssText = 'font-size:14px;line-height:1.5;letter-spacing:normal;text-transform:none;margin:0 0 18px;color:#555;';
        this.giftSection.append(explanation);
        const close = host.el('button', 'km-fbt__gift-close', '×');
        close.type = 'button'; close.setAttribute('aria-label', 'Close free gift options');
        close.addEventListener('click', () => this.giftSection.close());
        this.giftSection.append(close);
        this.giftSection.addEventListener('click', event => {
          if (event.target === this.giftSection) this.giftSection.close();
        });
        const entries = host.ownerDocument?.querySelectorAll('[data-km-fbt-gift-entry]') || [];
        for (const entry of entries) {
          if (entry.dataset.formId !== host.dataset.formId) continue;
          const label = entry.querySelector('.km-fbt__gift-entry-label');
          const action = entry.querySelector('.km-fbt__gift-entry-action');
          if (label) {
            label.textContent = 'Buy this item, get 1 free gift';
            const copy = host.el('span', 'km-fbt__gift-entry-copy');
            const note = host.el('span', 'km-fbt__gift-entry-note', 'Add to cart, then choose your free gift in the pop-up.');
            label.before(copy);
            copy.append(label, note);
          }
          if (action) action.textContent = 'View gifts';
          entry.href = `#${this.giftSection.id}`;
          entry.hidden = false;
          entry.addEventListener('click', event => {
            event.preventDefault();
            host.track?.('km_fbt_gift_entry_click');
            this.lastEntry = entry;
            this.giftSection.showModal();
            heading.focus({ preventScroll: true });
          });
          if (entry.nextElementSibling?.classList.contains('km-fbt__more-offers')) entry.nextElementSibling.remove();
        }
        this.giftSection.addEventListener('close', () => this.lastEntry?.focus({ preventScroll: true }));
        for (const choice of this.choices) this.renderChoice(choice);
        const moreOffers = host.el('div', 'km-fbt__more-offers');
        const moreText = host.el('span', 'km-fbt__more-offers-text', 'Looking for more Buy 1, Get 1 Free heated gear?');
        const moreLink = host.el('a', 'km-fbt__more-offers-link', 'Explore eligible products ›');
        moreLink.href = '/pages/heated-gear';
        moreOffers.append(moreText, moreLink);
        this.giftSection.append(moreOffers);
        host.append(this.giftSection);
      }
      if (this.offer) {
        this.nativeActivity = Date.now();
        this.nativeEvents = ['fg-cart:start-process', 'fg-cart:end-process', 'bogos:gift-check'];
        this.onNativeActivity = () => { this.nativeActivity = Date.now(); };
        for (const name of this.nativeEvents) document.addEventListener(name, this.onNativeActivity);
      }
      this.update();
    }
    destroy() {
      for (const name of this.nativeEvents || []) document.removeEventListener(name, this.onNativeActivity);
    }
    async waitForNativeQuiet() {
      const deadline = Date.now() + 5000;
      while (Date.now() - (this.nativeActivity || 0) < 500) {
        if (Date.now() >= deadline) throw Error('gift-engine-busy');
        await new Promise(resolve => scope.setTimeout(resolve, 100));
      }
    }
    renderChoice(choice) {
      const h = this.host;
      choice.selected = false;
      const display = h.el('article', 'km-gift-display');
      const header = h.el('div', 'km-gift-display__row');
      if (choice.product.image) {
        const image = h.el('img', 'km-gift-display__image');
        image.src = choice.product.image; image.alt = choice.product.title;
        image.loading = 'lazy'; image.width = 96; image.height = 112;
        header.append(image);
      }
      header.append(h.el('span', 'km-gift-display__title', choice.product.title));
      const amount = h.el('div', 'km-gift-display__price');
      amount.append(h.el('strong', '', h.money(0)));
      display.append(h.el('span', 'km-gift-display__badge', h.s.freeGift || 'FREE GIFT'));
      header.append(amount); display.append(header); this.giftSection.append(display);
      choice.card = display;
    }
    variant(choice) {
      const variant = this.logic.resolveVariant(choice.product.variants, choice.choices);
      return mappedGiftVariant(choice, variant, this.logic.isPurchasable) ? variant : null;
    }
    live(choice) { return choice.product.variants.some(v => mappedGiftVariant(choice, v, this.logic.isPurchasable)); }
    update() {
      this.updateQuantityOffer();
      if (this.banner) {
        const ready = bundleQuantity(this.host, this.bundle) >= this.bundle.minimumQuantity;
        this.banner.textContent = `${this.host.s.bundleOffer || 'Buy 2+ eligible items, save $50'} with code ${this.bundle.code}`;
        this.banner.classList.toggle('is-ready', ready);
      }
      for (const choice of this.choices) {
        choice.selected = false;
        choice.card.hidden = !this.live(choice);
      }
    }
    isReady() { this.update(); return true; }
    openPending() {}
    updateQuantityOffer() {
      const host = this.host, offer = this.quantityOffer;
      const active = quantityOfferActive(host, offer);
      if (active && !this.quantityBanner) {
        this.quantityBanner = host.el('div', 'km-fbt__quantity-banner');
        this.quantityHeadline = host.el('strong', 'km-fbt__quantity-headline');
        this.quantityCode = host.el('span', 'km-fbt__quantity-code');
        this.quantityBanner.append(this.quantityHeadline, this.quantityCode);
        this.quantityBanner.setAttribute('aria-live', 'polite');
        host.insertBefore(this.quantityBanner, this.banner || host.querySelector('.km-fbt__card'));
        this.quantityNotice = host.el('p', 'km-fbt__quantity-notice');
        host.insertBefore(this.quantityNotice, host.querySelector('.km-fbt__card'));
      }
      if (this.quantityBanner) this.quantityBanner.hidden = !active;
      if (this.quantityNotice) this.quantityNotice.hidden = !active;
      const qualified = active && quantityOfferQuantity(host, offer) >= offer.minimumQuantity;
      const copy = (template, fallback) => String(template || fallback).replace('{quantity}', String(offer?.minimumQuantity || 2)).replace('{percent}', String(offer?.discountPercentage || 10)).replace('{code}', String(offer?.code || 'HE10'));
      if (active) {
        this.quantityHeadline.textContent = copy(host.s.quantityOffer, 'Buy {quantity}+ heated items, save {percent}%');
        this.quantityCode.textContent = copy(host.s.quantityCode, 'CODE: {code}');
        this.quantityNotice.textContent = host.s.offersNotStackable || "Use one code at checkout. Offers cannot be combined.";
        this.quantityBanner.classList.toggle('is-ready', qualified);
      }
      for (const item of host.items) {
        const eligible = active && quantityOfferEligible(host, offer, item.product);
        item.price?.classList.toggle('km-fbt__price--quantity', eligible);
        if (!eligible || !item.price) continue;
        const possible = item.product.variants.filter(v => this.logic.isPurchasable(v) && v.options.every((value, index) => !item.choices[index] || value === item.choices[index]));
        const shown = item.variant || possible.sort((a, b) => a.price - b.price)[0] || item.product.variants[0];
        host.displayPrice(item.price, shown?.price || 0, qualified ? { type: 'DISCOUNT_PERCENT', value: offer.discountPercentage, code: offer.code } : {});
        let badge = item.price.querySelector('.km-fbt__badge');
        if (!badge) { badge = host.el('span', 'km-fbt__badge'); item.price.append(badge); }
        badge.classList.add('km-fbt__quantity-badge');
        badge.textContent = `${offer.discountPercentage}% OFF · ${copy(host.s.quantityCode, 'CODE: {code}')}`;
        badge.setAttribute('aria-label', `${offer.discountPercentage}% off with ${offer.code} when buying ${offer.minimumQuantity}+ eligible items`);
        item.price.append(host.el('span', 'km-fbt__quantity-price-note', qualified ? copy(host.s.quantityPriceNote, 'With {code} · Buy {quantity}+') : copy(host.s.quantityThreshold, 'Buy {quantity}+ eligible items')));
      }
    }
    priceCoupon(product) {
      if (quantityOfferActive(this.host, this.quantityOffer)) return {};
      return this.bundle && bundleQuantity(this.host, this.bundle) >= this.bundle.minimumQuantity ? {} : product.coupon;
    }
    refresh(fresh) {
      const oldQuantityOffer = this.quantityOffer;
      this.quantityOffer = fresh.quantityOffer;
      const oldBundle = this.bundle;
      this.bundle = fresh.bundleOffer;
      let decision = JSON.stringify(oldQuantityOffer || null) === JSON.stringify(this.quantityOffer || null) ? 'ready' : 'priceChanged';
      if (oldBundle && JSON.stringify(oldBundle) !== JSON.stringify(this.bundle)) decision = 'priceChanged';
      const selected = this.choices.find(c => c.selected);
      if (selected) {
        const match = fresh.giftOffer?.choices?.find(c => same(c.product.id, selected.product.id));
        if (!match || !same(fresh.giftOffer.offerRoot, this.offer?.offerRoot)) decision = 'unavailable';
      }
      this.offer = fresh.giftOffer;
      this.host.config.quantityOffer = fresh.quantityOffer; this.host.config.giftOffer = fresh.giftOffer; this.host.config.bundleOffer = fresh.bundleOffer;
      for (const choice of this.choices) {
        const match = fresh.giftOffer?.choices?.find(c => same(c.product.id, choice.product.id));
        if (match) { choice.product = match.product; choice.mappings = match.mappings; }
        else { choice.product = {...choice.product, variants: []}; choice.mappings = []; }
      }
      if (selected && !this.variant(selected)) decision = 'unavailable';
      this.update(); return decision;
    }
    async confirmGift() { return true; }
    usesNativeGiftFlow() { return false; }
    async addGift() {}
  }
  scope.KmFbtPromotions = KmFbtPromotions;
})(typeof window !== 'undefined' ? window : globalThis);
